import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { updateProspect, CRMProspect, CRMProspectStatus } from '../../../utils/crmScoringService';

interface EditProspectModalProps {
  prospect: CRMProspect;
  onClose: () => void;
  onSuccess: () => void;
}

export function EditProspectModal({ prospect, onClose, onSuccess }: EditProspectModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    organization_name: prospect.organization_name || '',
    sector: prospect.sector || '',
    contact_name: prospect.contact_name || '',
    contact_email: prospect.contact_email || '',
    contact_phone: prospect.contact_phone || '',
    source: prospect.source || '',
    estimated_cash: prospect.estimated_cash?.toString() || '',
    estimated_monthly_expenses: prospect.estimated_monthly_expenses?.toString() || '',
    next_major_due_date: prospect.next_major_due_date || '',
    next_major_due_amount: prospect.next_major_due_amount?.toString() || '',
    imf_score: prospect.imf_score?.toString() || '3',
    declic_reached: prospect.declic_reached || false,
    declic_phrase: prospect.declic_phrase || '',
    targeted_offer: prospect.targeted_offer || '',
    estimated_mrr: prospect.estimated_mrr?.toString() || '',
    status: prospect.status as CRMProspectStatus,
  });

  function handleChange(field: string, value: string | boolean) {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!formData.organization_name.trim()) {
        throw new Error('Le nom de l\'organisation est requis');
      }

      if (!prospect.id) {
        throw new Error('ID du prospect manquant');
      }

      await updateProspect(prospect.id, {
        organization_name: formData.organization_name.trim(),
        sector: formData.sector.trim() || undefined,
        contact_name: formData.contact_name.trim() || undefined,
        contact_email: formData.contact_email.trim() || undefined,
        contact_phone: formData.contact_phone.trim() || undefined,
        source: formData.source.trim() || undefined,
        estimated_cash: parseFloat(formData.estimated_cash) || 0,
        estimated_monthly_expenses: parseFloat(formData.estimated_monthly_expenses) || 0,
        next_major_due_date: formData.next_major_due_date || undefined,
        next_major_due_amount: parseFloat(formData.next_major_due_amount) || 0,
        imf_score: parseInt(formData.imf_score) as 1 | 2 | 3 | 4 | 5,
        declic_reached: formData.declic_reached,
        declic_phrase: formData.declic_phrase.trim() || undefined,
        targeted_offer: formData.targeted_offer.trim() || undefined,
        estimated_mrr: parseFloat(formData.estimated_mrr) || 0,
        status: formData.status,
      });

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Modifier le Prospect</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Informations Générales</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de l'organisation <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.organization_name}
                  onChange={(e) => handleChange('organization_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Secteur</label>
                <input
                  type="text"
                  value={formData.sector}
                  onChange={(e) => handleChange('sector', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: Association sportive, ESS, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                <input
                  type="text"
                  value={formData.source}
                  onChange={(e) => handleChange('source', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: LinkedIn, Référence, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du contact</label>
                <input
                  type="text"
                  value={formData.contact_name}
                  onChange={(e) => handleChange('contact_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => handleChange('contact_email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => handleChange('contact_phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="insomnie_detectee">Insomnie Détectée</option>
                  <option value="probleme_formule">Problème Formulé</option>
                  <option value="demo_realisee">Démo Réalisée</option>
                  <option value="projection_envoyee">Projection Envoyée</option>
                  <option value="decision_en_cours">Décision en Cours</option>
                  <option value="pilote_actif">Pilote Actif</option>
                  <option value="active">Client Actif</option>
                  <option value="perdu">Perdu</option>
                </select>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Données Financières</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trésorerie estimée (€)
                </label>
                <input
                  type="number"
                  value={formData.estimated_cash}
                  onChange={(e) => handleChange('estimated_cash', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="50000"
                  min="0"
                  step="100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dépenses mensuelles (€)
                </label>
                <input
                  type="number"
                  value={formData.estimated_monthly_expenses}
                  onChange={(e) => handleChange('estimated_monthly_expenses', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="15000"
                  min="0"
                  step="100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prochaine échéance majeure
                </label>
                <input
                  type="date"
                  value={formData.next_major_due_date}
                  onChange={(e) => handleChange('next_major_due_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Montant échéance (€)
                </label>
                <input
                  type="number"
                  value={formData.next_major_due_amount}
                  onChange={(e) => handleChange('next_major_due_amount', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="10000"
                  min="0"
                  step="100"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Comportement</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Score IMF (Immaturité Financière)
                </label>
                <select
                  value={formData.imf_score}
                  onChange={(e) => handleChange('imf_score', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="1">1 - Très faible maturité</option>
                  <option value="2">2 - Faible maturité</option>
                  <option value="3">3 - Maturité moyenne</option>
                  <option value="4">4 - Bonne maturité</option>
                  <option value="5">5 - Excellente maturité</option>
                </select>
              </div>

              <div className="flex items-center pt-6">
                <input
                  type="checkbox"
                  id="declic_reached"
                  checked={formData.declic_reached}
                  onChange={(e) => handleChange('declic_reached', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="declic_reached" className="ml-2 text-sm font-medium text-gray-700">
                  Déclic atteint
                </label>
              </div>

              {formData.declic_reached && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phrase du déclic
                  </label>
                  <textarea
                    value={formData.declic_phrase}
                    onChange={(e) => handleChange('declic_phrase', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={2}
                    placeholder="Ex: On ne sait plus où on en est financièrement..."
                  />
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Commercial</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Offre ciblée
                </label>
                <select
                  value={formData.targeted_offer}
                  onChange={(e) => handleChange('targeted_offer', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Sélectionner une offre</option>
                  <option value="Essentiel">Essentiel (49€/mois)</option>
                  <option value="Pro">Pro (149€/mois)</option>
                  <option value="DAF">DAF (349€/mois)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  MRR estimé (€/mois)
                </label>
                <input
                  type="number"
                  value={formData.estimated_mrr}
                  onChange={(e) => handleChange('estimated_mrr', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="49"
                  min="0"
                  step="1"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Le SPT et la probabilité de conversion seront recalculés automatiquement
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={loading}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
